import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { ApiMiddlewarePayload } from '@ocean/api'
import { ToastUtils } from '@ocean/utils'
import { AppThunk } from 'store'
import { createApiAction } from 'api'
import { LocalizationUtils } from 'utils'
import { userLoggedOut } from 'core/session/slices/session'
import { runCesPlayer, playerClose } from 'core/staIntegration/slices/staLegacyPlayer'
import {
  getCesDetails,
  getCesLockout,
} from 'modules/activityDetails/CesDetailedDetails/api/cesDetailedDetailsService'
import {
  CesDetailedDetailsState,
  ApiCesDetailedDetailsResponse,
  ApiCesDetailedLockoutResponse,
  RunCesParams,
} from './types'

const initialState: CesDetailedDetailsState = {
  cesDetailedDetailsData: {},
  cesDetailedLockoutData: {},
  forceReload: false,
  testId: null,
}

const cesDetailedDetailsSlice = createSlice({
  name: 'cesDetailedDetails',
  initialState,
  reducers: {
    cesDetailedDetailsLoaded(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiCesDetailedDetailsResponse>>
    ) {
      state.cesDetailedDetailsData = action.payload
      state.forceReload = false

      const { cesDetailedDetails } = action.payload.result || {}
      state.testId = cesDetailedDetails?.details.testId || null
    },
    clearCesDetailedDetails(state) {
      state.cesDetailedDetailsData = initialState.cesDetailedDetailsData
    },
    cesDetailedLockoutLoaded(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiCesDetailedLockoutResponse>>
    ) {
      state.cesDetailedLockoutData = action.payload
    },
  },
  extraReducers: builder => {
    builder
      .addCase(userLoggedOut, () => {
        return initialState
      })
      .addCase(playerClose, state => {
        state.forceReload = true
        return state
      })
  },
})

const { cesDetailedDetailsLoaded, clearCesDetailedDetails, cesDetailedLockoutLoaded } =
  cesDetailedDetailsSlice.actions

export { clearCesDetailedDetails }
export default cesDetailedDetailsSlice.reducer

export const loadCesDetailedDetailsData = (cesId: Guid, resultId?: Guid) => {
  return createApiAction({
    apiCalls: {
      cesDetailedDetails: () => getCesDetails(cesId, resultId),
    },
    payloadAction: cesDetailedDetailsLoaded,
  })
}

export const reloadCesDetailedDetailsData = (cesId: Guid, resultId?: Guid) => {
  return createApiAction({
    apiCalls: {
      cesDetailedDetails: () => getCesDetails(cesId, resultId),
    },
    payloadAction: cesDetailedDetailsLoaded,
    shouldShowGlobalLoading: true,
    shouldCallApi: ({ cesDetailedDetails }) => !cesDetailedDetails.cesDetailedDetailsData.isLoading,
    getResultToKeep: ({ cesDetailedDetails }) => cesDetailedDetails.cesDetailedDetailsData.result,
  })
}

export const openCes =
  (scheduleId?: Guid): AppThunk =>
  async (dispatch, getState) => {
    const { testId } = getState().cesDetailedDetails
    if (!testId) return ToastUtils.error(LocalizationUtils.localizeError('applicationError'))
    dispatch(runCesPlayer(testId, scheduleId))
  }

const runCes =
  (response: ApiMiddlewarePayload<ApiCesDetailedLockoutResponse>, params: RunCesParams): AppThunk =>
  async dispatch => {
    dispatch(cesDetailedLockoutLoaded(response))
    const { error, result } = response
    const { scheduleId } = params
    if (!error && !result?.cesDetailedLockout.isLockedOut) dispatch(openCes(scheduleId))
  }

export const loadCesDetailedLockoutData = (cesId: Guid, scheduleId?: Guid) => {
  return createApiAction({
    apiCalls: {
      cesDetailedLockout: () => getCesLockout(cesId),
    },
    payloadAction: cesDetailedLockoutLoaded,
    nextAction: {
      action: runCes,
      params: { scheduleId },
    },
    shouldShowGlobalLoading: true,
    enableErrorNotification: true,
  })
}
